/**
 * Pure AnalysisSpec → MapLibre source/layer builders for the M8 overlay set,
 * the shared analysis layers added to the M4 GPU render path. Like mapLayers.ts
 * this has NO maplibre-gl import — everything is plain GeoJSON + JSON paint
 * expressions built from tokens, so the renderer stays a thin effect layer and
 * these stay unit-testable. Because they are a pure function of
 * `{PointSet, AnalysisSpec}` (never `mode`), editor and M6 embed produce the
 * SAME overlay output for the same spec (the one-spec / embed-fidelity contract).
 *
 * Territory fills/outlines render BENEATH the markers; territory labels and
 * radius circles' outlines render above — the renderer inserts them at the
 * matching seams.
 */

import { circleRing, convexHull, ringCentroid, type Ring } from "@/lib/analysis/geometry";
import { resolveCenter } from "@/lib/analysis/radius";
import { DRIVE_TIME_FILL_OPACITY, driveTimeBandLabel, routeTotalsLabel } from "@/lib/analysis";
import type { MapExpr, MapTokens, AnalysisSpec, PointSet, Territory } from "@/lib/map";

/* Reserved source/layer ids — one declarative overlay set, driven by the spec. */
export const ANALYSIS_TERRITORY_SOURCE = "analysis-territories";
export const ANALYSIS_TERRITORY_LABEL_SOURCE = "analysis-territory-labels";
export const ANALYSIS_RADIUS_SOURCE = "analysis-radii";
export const ANALYSIS_DRIVETIME_SOURCE = "analysis-drive-time";
export const ANALYSIS_ROUTE_SOURCE = "analysis-route";
export const ANALYSIS_ROUTE_STOP_SOURCE = "analysis-route-stops";
export const ANALYSIS_DRAW_SOURCE = "analysis-draw";

export const LAYER_TERRITORY_FILL = "analysis-territory-fill";
export const LAYER_TERRITORY_LINE = "analysis-territory-line";
export const LAYER_TERRITORY_LABEL = "analysis-territory-label";
export const LAYER_RADIUS_FILL = "analysis-radius-fill";
export const LAYER_RADIUS_LINE = "analysis-radius-line";
export const LAYER_DRIVETIME_FILL = "analysis-drive-time-fill";
export const LAYER_DRIVETIME_LINE = "analysis-drive-time-line";
export const LAYER_ROUTE_LINE = "analysis-route-line";
export const LAYER_ROUTE_STOP = "analysis-route-stop";
export const LAYER_ROUTE_STOP_LABEL = "analysis-route-stop-label";
export const LAYER_DRAW_FILL = "analysis-draw-fill";
export const LAYER_DRAW_LINE = "analysis-draw-line";

/** Overlay layers that render BENEATH the point markers (fills + outlines + circles). */
export const ANALYSIS_UNDER_LAYERS = [
  LAYER_DRIVETIME_FILL,
  LAYER_DRIVETIME_LINE,
  LAYER_TERRITORY_FILL,
  LAYER_TERRITORY_LINE,
  LAYER_RADIUS_FILL,
  LAYER_RADIUS_LINE,
] as const;
/** Overlay layers that render ABOVE the markers (labels, route, transient draw). */
export const ANALYSIS_OVER_LAYERS = [
  LAYER_TERRITORY_LABEL,
  LAYER_ROUTE_LINE,
  LAYER_ROUTE_STOP,
  LAYER_ROUTE_STOP_LABEL,
  LAYER_DRAW_FILL,
  LAYER_DRAW_LINE,
] as const;
export const ANALYSIS_LAYERS = [...ANALYSIS_UNDER_LAYERS, ...ANALYSIS_OVER_LAYERS];
export const ANALYSIS_SOURCES = [
  ANALYSIS_TERRITORY_SOURCE,
  ANALYSIS_TERRITORY_LABEL_SOURCE,
  ANALYSIS_RADIUS_SOURCE,
  ANALYSIS_DRIVETIME_SOURCE,
  ANALYSIS_ROUTE_SOURCE,
  ANALYSIS_ROUTE_STOP_SOURCE,
  ANALYSIS_DRAW_SOURCE,
];

/** Overlay opacities (unitless floats — not spacing/color tokens). */
const TERRITORY_FILL_OPACITY = 0.16;
const TERRITORY_LINE_WIDTH = 2;
const RADIUS_FILL_OPACITY = 0.08;
const RADIUS_LINE_WIDTH = 1.5;
const DRIVETIME_LINE_WIDTH = 1.5;
const ROUTE_LINE_WIDTH = 3;
const ROUTE_STOP_RADIUS = 11;
const ROUTE_STOP_STROKE_WIDTH = 2;

interface PolygonFC {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: { type: "Polygon"; coordinates: [Ring] };
    properties: Record<string, string | number>;
  }>;
}
interface PointFC {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: { type: "Point"; coordinates: [number, number] };
    properties: Record<string, string | number>;
  }>;
}
interface LineFC {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: { type: "LineString"; coordinates: [number, number][] };
    properties: Record<string, string | number>;
  }>;
}

const emptyPolygonFC = (): PolygonFC => ({ type: "FeatureCollection", features: [] });
const emptyPointFC = (): PointFC => ({ type: "FeatureCollection", features: [] });
const emptyLineFC = (): LineFC => ({ type: "FeatureCollection", features: [] });

/** Resolve a territory's render ring: its stored polygon, else its members' hull. */
function territoryRing(territory: Territory, pointSet: PointSet): Ring | null {
  if (territory.polygon && territory.polygon.length >= 3) return territory.polygon;
  const members = pointSet.points.filter((p) => territory.memberRowIds.includes(p.id));
  const hull = convexHull(members);
  return hull.length >= 4 ? hull : null;
}

/** Territory polygons → a fill/outline FeatureCollection (color carried per feature). */
export function territoryFeatures(pointSet: PointSet, analysis: AnalysisSpec | undefined): PolygonFC {
  const items = analysis?.territories?.items ?? [];
  const fc = emptyPolygonFC();
  for (const t of items) {
    const ring = territoryRing(t, pointSet);
    if (!ring) continue;
    fc.features.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [ring] },
      properties: { _id: t.id, color: t.color, name: t.name, count: t.memberRowIds.length },
    });
  }
  return fc;
}

/** Territory label points (centroid of each rendered ring). */
export function territoryLabelFeatures(pointSet: PointSet, analysis: AnalysisSpec | undefined): PointFC {
  const items = analysis?.territories?.items ?? [];
  const fc = emptyPointFC();
  for (const t of items) {
    const ring = territoryRing(t, pointSet);
    const c = ring ? ringCentroid(ring) : null;
    if (!c) continue;
    fc.features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: c },
      properties: { _id: t.id, text: t.name },
    });
  }
  return fc;
}

/** Radius circles → polygon rings (center resolved from coord or rowId). */
export function radiusFeatures(pointSet: PointSet, analysis: AnalysisSpec | undefined): PolygonFC {
  const radii = analysis?.radii ?? [];
  const fc = emptyPolygonFC();
  for (const r of radii) {
    const center = resolveCenter(r.center, pointSet);
    if (!center) continue;
    fc.features.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [circleRing(center, r.radiusMeters)] },
      properties: { _id: r.id, count: r.insideRowIds.length },
    });
  }
  return fc;
}

/** Drive-time isochrone bands → polygon rings (persisted color/opacity per band). */
export function driveTimeFeatures(analysis: AnalysisSpec | undefined): PolygonFC {
  const bands = analysis?.driveTime?.bands ?? [];
  const fc = emptyPolygonFC();
  // Emitted in the spec's band order; per-feature opacity blends the overlaps.
  bands.forEach((b, i) => {
    if (!b.polygon || b.polygon.length < 3) return;
    const props: Record<string, string | number> = {
      _id: i,
      opacity: b.style?.opacity ?? DRIVE_TIME_FILL_OPACITY,
      label: driveTimeBandLabel(b, i),
    };
    if (b.style?.color) props.color = b.style.color;
    fc.features.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [b.polygon as Ring] },
      properties: props,
    });
  });
  return fc;
}

/** Ordered route → one LineString through the stops' resolved coordinates. */
export function routeLineFeatures(pointSet: PointSet, analysis: AnalysisSpec | undefined): LineFC {
  const routes = analysis?.routes;
  const fc = emptyLineFC();
  if (!routes || routes.stopRowIds.length < 2) return fc;
  const byId = new Map(pointSet.points.map((p) => [p.id, p]));
  const coords: [number, number][] = [];
  for (const id of routes.stopRowIds) {
    const p = byId.get(id);
    if (p) coords.push([p.lng, p.lat]);
  }
  if (coords.length >= 2) {
    fc.features.push({ type: "Feature", geometry: { type: "LineString", coordinates: coords }, properties: {} });
  }
  return fc;
}

/** Ordered route stops → numbered points (order label carried per feature). */
export function routeStopFeatures(pointSet: PointSet, analysis: AnalysisSpec | undefined): PointFC {
  const routes = analysis?.routes;
  const fc = emptyPointFC();
  if (!routes) return fc;
  const byId = new Map(pointSet.points.map((p) => [p.id, p]));
  routes.stopRowIds.forEach((id, i) => {
    const p = byId.get(id);
    if (!p) return;
    fc.features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: { _id: id, order: i + 1, text: String(i + 1) },
    });
  });
  return fc;
}

/** A transient draw-preview ring (lasso in progress, or the radius being sized). */
export function drawFeatures(ring: Ring | null): PolygonFC {
  const fc = emptyPolygonFC();
  if (ring && ring.length >= 3) {
    fc.features.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [ring] },
      properties: {},
    });
  }
  return fc;
}

/* ------------------------------------------------------------------ paint */

export function territoryFillPaint(): Record<string, MapExpr> {
  return { "fill-color": ["get", "color"], "fill-opacity": TERRITORY_FILL_OPACITY };
}
export function territoryLinePaint(): Record<string, MapExpr> {
  return { "line-color": ["get", "color"], "line-width": TERRITORY_LINE_WIDTH, "line-opacity": 0.9 };
}
export function territoryLabelPaint(tokens: MapTokens): Record<string, MapExpr> {
  return {
    "text-color": tokens.label.ink,
    "text-halo-color": tokens.label.halo,
    "text-halo-width": 1.5,
    "text-halo-blur": 0.5,
  };
}
export function radiusFillPaint(tokens: MapTokens): Record<string, MapExpr> {
  return { "fill-color": tokens.marker.selectedHalo, "fill-opacity": RADIUS_FILL_OPACITY };
}
export function radiusLinePaint(tokens: MapTokens): Record<string, MapExpr> {
  return {
    "line-color": tokens.marker.selectedRing,
    "line-width": RADIUS_LINE_WIDTH,
    "line-dasharray": [2, 2],
    "line-opacity": 0.9,
  };
}
export function driveTimeFillPaint(tokens: MapTokens): Record<string, MapExpr> {
  return {
    "fill-color": ["coalesce", ["get", "color"], tokens.marker.selectedHalo],
    "fill-opacity": ["coalesce", ["get", "opacity"], DRIVE_TIME_FILL_OPACITY],
  };
}
export function driveTimeLinePaint(tokens: MapTokens): Record<string, MapExpr> {
  return {
    "line-color": ["coalesce", ["get", "color"], tokens.marker.selectedRing],
    "line-width": DRIVETIME_LINE_WIDTH,
    "line-opacity": 0.7,
  };
}
export function routeLinePaint(tokens: MapTokens): Record<string, MapExpr> {
  return { "line-color": tokens.marker.selectedRing, "line-width": ROUTE_LINE_WIDTH, "line-opacity": 0.9 };
}
export function routeStopPaint(tokens: MapTokens): Record<string, MapExpr> {
  return {
    "circle-radius": ROUTE_STOP_RADIUS,
    "circle-color": tokens.marker.fill,
    "circle-stroke-color": tokens.marker.ring,
    "circle-stroke-width": ROUTE_STOP_STROKE_WIDTH,
  };
}
export function routeStopLabelPaint(tokens: MapTokens): Record<string, MapExpr> {
  return { "text-color": tokens.cluster.text };
}
export function drawFillPaint(tokens: MapTokens): Record<string, MapExpr> {
  return { "fill-color": tokens.marker.selectedHalo, "fill-opacity": 0.12 };
}
export function drawLinePaint(tokens: MapTokens): Record<string, MapExpr> {
  return { "line-color": tokens.marker.selectedRing, "line-width": 2, "line-dasharray": [1.5, 1.5] };
}

/* ------------------------------------------------------------- summaries */

export interface TerritorySummaryRow {
  id: string;
  name: string;
  color: string;
  count: number;
  sum?: number;
  balanceBy?: string;
}
export interface RadiusSummaryRow {
  id: string;
  label: string;
  count: number;
}
export interface DriveTimeSummaryRow {
  id: string;
  label: string;
  color?: string;
}
export interface RouteSummaryRow {
  /** Ordered stop count. */
  stops: number;
  /** "38 mi · 46 min" totals, or null when not yet optimized. */
  totalsLabel: string | null;
}

/** Territory rollup rows for the shared summary panel. */
export function territorySummary(analysis: AnalysisSpec | undefined): TerritorySummaryRow[] {
  return (analysis?.territories?.items ?? []).map((t) => ({
    id: t.id,
    name: t.name,
    color: t.color,
    count: t.rollup?.count ?? t.memberRowIds.length,
    sum: t.rollup?.sum,
    balanceBy: t.rollup?.balanceBy,
  }));
}

/** Radius rows (points-in-radius counts) for the shared summary panel. */
export function radiusSummary(analysis: AnalysisSpec | undefined): RadiusSummaryRow[] {
  return (analysis?.radii ?? []).map((r, i) => ({
    id: r.id,
    label: r.label ?? `Circle ${i + 1}`,
    count: r.insideRowIds.length,
  }));
}

/** Drive-time band rows (label + persisted color) for the summary/legend. */
export function driveTimeSummary(analysis: AnalysisSpec | undefined): DriveTimeSummaryRow[] {
  return (analysis?.driveTime?.bands ?? []).map((b, i) => ({
    id: `band-${i}`,
    label: driveTimeBandLabel(b, i),
    color: b.style?.color,
  }));
}

/** Route totals row for the summary panel (null totals until optimized). */
export function routeSummary(analysis: AnalysisSpec | undefined): RouteSummaryRow | null {
  const routes = analysis?.routes;
  if (!routes || routes.stopRowIds.length === 0) return null;
  return {
    stops: routes.stopRowIds.length,
    totalsLabel: routes.totals ? routeTotalsLabel(routes.totals) : null,
  };
}
